/**
 * Dependency-free PNG tEXt / zTXt reader for SillyTavern character card chunks.
 * Prefer `ccv3` over `chara` when both are present.
 */

const PNG_SIG = new Uint8Array([137, 80, 78, 71, 13, 10, 26, 10]);

function bytesEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

function readLatin1(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]!);
  return s;
}

function decodeBase64(b64: string): string {
  const cleaned = b64.replace(/\s+/g, '');
  const Buf = (globalThis as unknown as { Buffer?: { from(s: string, enc: string): { toString(enc: string): string } } })
    .Buffer;
  if (Buf) return Buf.from(cleaned, 'base64').toString('utf8');
  const binary = atob(cleaned);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new TextDecoder().decode(bytes);
}

/** Inflate zlib-wrapped zTXt payload via the Web Streams DecompressionStream API. */
async function inflateZlib(data: Uint8Array): Promise<Uint8Array> {
  const DS = (globalThis as unknown as {
    DecompressionStream?: new (format: string) => { readable: ReadableStream; writable: WritableStream };
  }).DecompressionStream;
  if (typeof DS !== 'function') {
    throw new Error('Cannot inflate zTXt chunk: DecompressionStream unavailable');
  }
  // PNG zTXt uses a zlib wrapper; 'deflate' accepts zlib headers in browsers and Node 20+.
  const ds = new DS('deflate');
  // Copy into a fresh ArrayBuffer-backed view so BlobPart typing accepts it under DOM libs.
  const copy = new Uint8Array(data.byteLength);
  copy.set(data);
  const stream = new Blob([copy]).stream().pipeThrough(ds);
  const buf = await new Response(stream).arrayBuffer();
  return new Uint8Array(buf);
}

function findNull(data: Uint8Array, start = 0): number {
  for (let i = start; i < data.length; i++) if (data[i] === 0) return i;
  return -1;
}

async function decodeTextChunk(type: string, data: Uint8Array): Promise<{ key: string; value: string } | null> {
  if (type === 'tEXt') {
    const sep = findNull(data);
    if (sep < 0) return null;
    const key = readLatin1(data.subarray(0, sep));
    const value = readLatin1(data.subarray(sep + 1));
    return { key, value };
  }
  if (type === 'zTXt') {
    const sep = findNull(data);
    if (sep < 0 || sep + 2 > data.length) return null;
    const key = readLatin1(data.subarray(0, sep));
    const method = data[sep + 1]!;
    if (method !== 0) return null;
    const inflated = await inflateZlib(data.subarray(sep + 2));
    return { key, value: readLatin1(inflated) };
  }
  return null;
}

/**
 * Extract SillyTavern card JSON string from a PNG buffer.
 * Returns null when no `chara` / `ccv3` chunk is found.
 */
export async function extractSillyTavernJsonFromPng(bytes: Uint8Array): Promise<string | null> {
  if (bytes.length < 8 || !bytesEqual(bytes.subarray(0, 8), PNG_SIG)) {
    throw new Error('Not a PNG file');
  }
  let offset = 8;
  let chara: string | null = null;
  let ccv3: string | null = null;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);

  while (offset + 12 <= bytes.length) {
    const length = view.getUint32(offset, false);
    const type = readLatin1(bytes.subarray(offset + 4, offset + 8));
    const dataStart = offset + 8;
    const dataEnd = dataStart + length;
    if (dataEnd + 4 > bytes.length) break;
    const data = bytes.subarray(dataStart, dataEnd);
    if (type === 'tEXt' || type === 'zTXt') {
      const decoded = await decodeTextChunk(type, data);
      if (decoded) {
        if (decoded.key === 'ccv3') ccv3 = decoded.value;
        else if (decoded.key === 'chara') chara = decoded.value;
      }
    }
    if (type === 'IEND') break;
    offset = dataEnd + 4; // CRC
  }

  const encoded = ccv3 ?? chara;
  if (!encoded) return null;
  try {
    return decodeBase64(encoded);
  } catch {
    // Some tools store raw JSON in the chunk.
    const trimmed = encoded.trim();
    if (trimmed.startsWith('{')) return trimmed;
    throw new Error('PNG card chunk is not valid base64 or JSON');
  }
}
